import { authService } from './auth';
import { errorHandler } from './errorHandler';

export interface EmailSearchFilters {
  searchIn?: 'subject' | 'body' | 'from' | 'to' | 'all';
  since?: string;
  before?: string;
  hasAttachments?: boolean;
  unread?: boolean;
  folders?: string[];
}

export interface EmailSearchParams extends EmailSearchFilters {
  query: string;
  limit?: number;
  page?: number;
}

export interface EmailMessage {
  id: string;
  uid: number;
  folder: string;
  headers: {
    from: string;
    to: string;
    subject: string;
    date: string;
  };
  body: {
    text: string;
    html: string;
  };
  flags: string[];
  size: number;
}

export interface SearchMessages {
    uid: string;
    id: string;
    seqno: string;
    headers: {
        from: string;
        to: string;
        subject: string;
        date: string;
    };
    body: string;
    html: string;
    raw: string;
    date: string;
    folderPath: string;
}

export interface SearchResponse {
  messages: SearchMessages[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

class SearchService {
  private baseUrl = `${authService.API_BASE_URL}/api/email/advanced-search`;
  private searchControllers: Map<string, AbortController> = new Map();

  private getHeaders() {
    const token = authService.getAccessToken();
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  private getSearchKey(params: EmailSearchParams): string {
    return JSON.stringify(params);
  }

  cancelAllSearchRequests() {
    for (const [key, controller] of this.searchControllers) {
      controller.abort();
      this.searchControllers.delete(key);
    }
  }

  async searchEmails(params: EmailSearchParams): Promise<SearchResponse> {
    try {
      const searchKey = this.getSearchKey(params);

      // Cancel all ongoing search requests
      this.cancelAllSearchRequests();

      // Create a new AbortController for this request
      const controller = new AbortController();
      this.searchControllers.set(searchKey, controller);

      const response = await fetch(`${this.baseUrl}`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(params),
        signal: controller.signal
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to search emails');
      }

      const result = await response.json();
      
      // Clean up the controller after successful request
      this.searchControllers.delete(searchKey);
      
      return result;
    } catch (error) {
      // Don't throw error if request was aborted
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          throw new Error('Search request cancelled');
        }
        // Check if it's a network error
        if (!window.navigator.onLine || error.message.includes('Network') || error.message.includes('Failed to fetch')) {
          errorHandler.showNetworkError();
        }
      }
      console.error('Search error:', error);
      throw error;
    }
  }
}

export const searchService = new SearchService(); 